import { useMemo } from "react";
import moment from "moment-timezone";
import { Box, Chip, Typography, keyframes } from "@mui/material";
import { blueGrey, teal, green, amber, red } from "@mui/material/colors";
import {
  IconMoodHappy,
  IconMoodSmile,
  IconMoodSmileBeam,
  IconMoodPuzzled,
  IconAsterisk,
} from "@tabler/icons-react";
import { LuBicepsFlexed } from "react-icons/lu";
import TimeUtils from "utils/time";

export interface UserData {
  location?: {
    city?: string;
    formatted?: string;
  } | null;
  hasPublicImages?: boolean;
  extraSupportRequested?: boolean;
  intact?: boolean;
  lastTugged?: Date | string;
  timezone?: string;
  active?: boolean;
}

export interface UserCardChipsProps {
  user: UserData;
  inProgress?: boolean;
  lastActivity?: Date | string | null;
  signedIn?: boolean;
}

// Pulse animation for the biceps icon
const pulseAnimation = keyframes`
  0% {
    opacity: 0.9;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0.9;
    transform: scale(1);
  }
`;

const UserCardChips = ({
  user,
  inProgress = false,
  lastActivity = null,
  signedIn = false,
}: UserCardChipsProps) => {
  const locationDisplay = signedIn ? user.location?.formatted : undefined;

  // Calculate activity status
  const activityChip = useMemo(() => {
    // If actively tugging. `inProgress` is already recency-gated by UserCard;
    // don't OR in raw `user.active` or a stale session pulses "Tugging Now".
    if (inProgress) {
      return {
        label: "Tugging Now",
        backgroundColor: teal[100],
        color: teal[700],
        icon: (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
            }}
          >
            <LuBicepsFlexed size={12} color={teal[700]} />
          </Box>
        ),
      };
    }

    // Calculate time since last tugged
    const lastTuggedSource = lastActivity || user.lastTugged;
    if (!lastTuggedSource) {
      return null; // New user, no activity chip
    }

    const dateToCompare = lastActivity
      ? lastActivity
      : TimeUtils.inThisTimezone(user.lastTugged!, user.timezone || null);
    const dateMoment = moment(dateToCompare);
    const now = moment();
    const elapsed = now.diff(dateMoment);

    if (isNaN(elapsed) || elapsed < 0) {
      return null;
    }

    // Calculate display string
    const breakdown = TimeUtils.breakdown(elapsed);
    const display = TimeUtils.elapsedToString(breakdown, 1);

    const sixHours = moment.duration(6, "hours").asMilliseconds();
    const oneDay = moment.duration(1, "days").asMilliseconds();
    const threeDays = moment.duration(3, "days").asMilliseconds();

    let backgroundColor: string;
    let color: string;
    let IconComponent: typeof IconMoodHappy;

    if (elapsed < sixHours) {
      backgroundColor = green[100];
      color = green[700];
      IconComponent = IconMoodHappy;
    } else if (elapsed < oneDay) {
      backgroundColor = green[100];
      color = green[700];
      IconComponent = IconMoodSmileBeam;
    } else if (elapsed < threeDays) {
      backgroundColor = amber[100];
      color = amber[800];
      IconComponent = IconMoodSmile;
    } else {
      backgroundColor = red[100];
      color = red[700];
      IconComponent = IconMoodPuzzled;
    }

    return {
      label: display === "0" ? "Just Tugged!" : `Tugged ${display} ago`,
      backgroundColor,
      color,
      icon: <IconComponent size={14} color={color} />,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.lastTugged, user.timezone, inProgress]);

  const hasChips =
    locationDisplay || user.hasPublicImages || user.intact || activityChip;

  if (!hasChips) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Location */}
      {locationDisplay && (
        <Typography
          variant="body2"
          sx={{
            color: blueGrey[600],
            fontSize: "0.75rem",
          }}
        >
          📍 {locationDisplay}
        </Typography>
      )}

      {/* Activity Chip */}
      {activityChip && (
        <Chip
          icon={activityChip.icon || undefined}
          label={activityChip.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            backgroundColor: activityChip.backgroundColor,
            color: activityChip.color,
            "& .MuiChip-label": { px: 1 },
            "& .MuiChip-icon": { ml: 0.5 },
          }}
        />
      )}

      {/* Needs Support: hidden -- the legacy user-doc flag is stale and unreliable.
          Community support will be reintroduced from a dedicated table later. */}

      {/* Intact */}
      {user.intact && (
        <Chip
          icon={<IconAsterisk size={12} color={amber[900]} />}
          label="Intact"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            backgroundColor: amber[100],
            color: amber[900],
            "& .MuiChip-label": { px: 1 },
          }}
        />
      )}
    </Box>
  );
};

export default UserCardChips;
